import { useState, useRef, useCallback } from 'react';

/**
 * Hook for reading RFID tags from UHF-105 / S4A reader via Web Serial API.
 * 
 * The reader sends data as: STX(0x02) + ASCII decimal digits + CR(0x0D)
 * Example: 0x02 "1974705192" 0x0D
 * 
 * Tags are filtered by a minimum read count to reduce noise.
 */
const useSerialReader = ({ minReads = 2, onNewTag } = {}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isReading, setIsReading] = useState(false);
  const [tags, setTags] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [error, setError] = useState(null);

  const portRef = useRef(null);
  const readerRef = useRef(null);
  const readLoopRef = useRef(false);
  const tagCountsRef = useRef(new Map());   // tag -> read count
  const confirmedRef = useRef(new Set());   // confirmed tags
  const minReadsRef = useRef(minReads);
  const bufferRef = useRef('');             // data buffer for frame assembly
  const onNewTagRef = useRef(onNewTag);

  // Keep refs in sync
  minReadsRef.current = minReads;
  onNewTagRef.current = onNewTag;

  /**
   * Check if Web Serial API is supported
   */
  const isSupported = typeof navigator !== 'undefined' && 'serial' in navigator;

  /**
   * Parse incoming bytes: extract frames between STX(0x02) and CR(0x0D)
   */
  const processChunk = useCallback((chunk) => {
    // chunk is a Uint8Array
    // Convert to string (binary), append to buffer
    let str = '';
    for (let i = 0; i < chunk.length; i++) {
      str += String.fromCharCode(chunk[i]);
    }
    bufferRef.current += str;

    const STX = '\x02';
    const CR = '\x0D';
    let newConfirmed = [];
    let safety = 0;

    while (safety++ < 200) {
      const stxIdx = bufferRef.current.indexOf(STX);
      if (stxIdx === -1) {
        bufferRef.current = '';
        break;
      }
      const crIdx = bufferRef.current.indexOf(CR, stxIdx + 1);
      if (crIdx === -1) {
        bufferRef.current = bufferRef.current.substring(stxIdx);
        break;
      }

      const content = bufferRef.current.substring(stxIdx + 1, crIdx);
      bufferRef.current = bufferRef.current.substring(crIdx + 1);

      // Clean: keep only digits
      const cleaned = content.replace(/[^0-9]/g, '');
      if (cleaned.length >= 6) {
        const count = (tagCountsRef.current.get(cleaned) || 0) + 1;
        tagCountsRef.current.set(cleaned, count);

        if (count >= minReadsRef.current && !confirmedRef.current.has(cleaned)) {
          confirmedRef.current.add(cleaned);
          newConfirmed.push(cleaned);
        }
      }
    }

    // Update pending count
    setPendingCount(tagCountsRef.current.size - confirmedRef.current.size);

    // Add newly confirmed tags
    if (newConfirmed.length > 0) {
      setTags((prev) => [...newConfirmed, ...prev]);
      newConfirmed.forEach((tag) => {
        if (onNewTagRef.current) onNewTagRef.current(tag);
      });
    }
  }, []);

  /**
   * Read loop: continuously read from serial port
   */
  const readLoop = useCallback(async (reader) => {
    try {
      while (readLoopRef.current) {
        const { value, done } = await reader.read();
        if (done) break;
        if (value) processChunk(value);
      }
    } catch (err) {
      if (readLoopRef.current) {
        console.error('Serial read error:', err);
        setError(err.message);
      }
    }
  }, [processChunk]);

  /**
   * Connect to serial port and start reading
   */
  const connect = useCallback(async (baudRate = 115200) => {
    if (!isSupported) {
      setError('Web Serial API no soportada. Usa Chrome o Edge.');
      return false;
    }

    try {
      setError(null);

      // Request port from user (browser shows picker dialog)
      const port = await navigator.serial.requestPort();
      await port.open({ baudRate });

      portRef.current = port;
      setIsConnected(true);

      // Clear state
      tagCountsRef.current.clear();
      confirmedRef.current.clear();
      bufferRef.current = '';
      setTags([]);
      setPendingCount(0);

      // Start reading
      readLoopRef.current = true;
      setIsReading(true);

      const reader = port.readable.getReader();
      readerRef.current = reader;
      readLoop(reader);

      return true;
    } catch (err) {
      console.error('Serial connect error:', err);
      setError(err.message);
      setIsConnected(false);
      setIsReading(false);
      return false;
    }
  }, [isSupported, readLoop]);

  /**
   * Disconnect from serial port
   */
  const disconnect = useCallback(async () => {
    readLoopRef.current = false;

    try {
      if (readerRef.current) {
        await readerRef.current.cancel();
        readerRef.current.releaseLock();
        readerRef.current = null;
      }
    } catch (e) {
      console.warn('Reader cancel error:', e);
    }

    try {
      if (portRef.current) {
        await portRef.current.close();
        portRef.current = null;
      }
    } catch (e) {
      console.warn('Port close error:', e);
    }

    setIsConnected(false);
    setIsReading(false);
  }, []);

  /**
   * Clear all collected tags
   */
  const clearTags = useCallback(() => {
    tagCountsRef.current.clear();
    confirmedRef.current.clear();
    bufferRef.current = '';
    setTags([]);
    setPendingCount(0);
  }, []);

  /**
   * Update minimum reads filter on the fly
   */
  const setMinReads = useCallback((n) => {
    minReadsRef.current = n;
    // Re-check pending tags that might now qualify
    let newConfirmed = [];
    for (const [tag, count] of tagCountsRef.current.entries()) {
      if (count >= n && !confirmedRef.current.has(tag)) {
        confirmedRef.current.add(tag);
        newConfirmed.push(tag);
      }
    }
    setPendingCount(tagCountsRef.current.size - confirmedRef.current.size);
    if (newConfirmed.length > 0) {
      setTags((prev) => [...newConfirmed, ...prev]);
    }
  }, []);

  /**
   * Send raw bytes to the reader (for power/buzzer commands)
   * UHF-105 protocol: BB [type] [cmd] [paramLen_hi] [paramLen_lo] [params...] [checksum] 7E
   */
  const sendCommand = useCallback(async (hexString) => {
    if (!portRef.current || !portRef.current.writable) return false;
    try {
      const bytes = new Uint8Array(hexString.match(/.{2}/g).map((b) => parseInt(b, 16)));
      const writer = portRef.current.writable.getWriter();
      await writer.write(bytes);
      writer.releaseLock();
      return true;
    } catch (err) {
      console.error('Send command error:', err);
      return false;
    }
  }, []);

  /**
   * Calculate checksum for UHF-105 command
   */
  const calcChecksum = (hexBody) => {
    const bytes = hexBody.match(/.{2}/g) || [];
    let sum = 0;
    for (const b of bytes) sum += parseInt(b, 16);
    return (sum & 0xFF).toString(16).padStart(2, '0').toUpperCase();
  };

  /**
   * Set reader TX power (1-30 dBm)
   */
  const setPower = useCallback(async (powerDbm) => {
    if (powerDbm < 1 || powerDbm > 30) return false;
    const powerVal = powerDbm * 100;
    const hi = ((powerVal >> 8) & 0xFF).toString(16).padStart(2, '0');
    const lo = (powerVal & 0xFF).toString(16).padStart(2, '0');
    const body = `00B60002${hi}${lo}`;
    const cmd = `BB${body}${calcChecksum(body)}7E`;
    return sendCommand(cmd);
  }, [sendCommand]);

  /**
   * Enable/disable buzzer
   */
  const setBuzzer = useCallback(async (enabled) => {
    const val = enabled ? '01' : '00';
    const body = `00B00001${val}`;
    const cmd = `BB${body}${calcChecksum(body)}7E`;
    return sendCommand(cmd);
  }, [sendCommand]);

  return {
    isSupported,
    isConnected,
    isReading,
    tags,
    pendingCount,
    error,
    connect,
    disconnect,
    clearTags,
    setMinReads,
    setPower,
    setBuzzer,
    sendCommand,
  };
};

export default useSerialReader;
